<script>
import CustomModulesAPI from 'dashboard/api/customModules';
import { useMapGetter } from 'dashboard/composables/store';

// Evita retries en bucle si el enlace embebido sigue fallando por otra razón
// (tenant inactivo, secreto mal configurado, etc.), no solo por expiración.
const MAX_AUTO_RETRIES = 2;
const RETRY_COOLDOWN_MS = 10000;

export default {
  setup() {
    const customModules = useMapGetter('customModules/getModulesOnSidebar');
    return { customModules };
  },
  data() {
    return {
      signedUrl: '',
      isLoading: true,
      hasError: false,
      retryCount: 0,
      lastRetryAt: 0,
    };
  },
  computed: {
    currentModule() {
      const id = Number(this.$route.params.moduleId);
      return this.customModules.find(item => item.id === id) || {};
    },
  },
  watch: {
    '$route.params.moduleId': {
      immediate: true,
      handler() {
        this.retryCount = 0;
        this.fetchSignedUrl();
      },
    },
  },
  mounted() {
    window.addEventListener('message', this.handleEmbedMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.handleEmbedMessage);
  },
  methods: {
    async fetchSignedUrl() {
      this.isLoading = true;
      this.hasError = false;
      this.signedUrl = '';
      try {
        const { moduleId } = this.$route.params;
        const response = await CustomModulesAPI.show(moduleId);
        this.signedUrl = response.data.url;
      } catch (error) {
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
    handleEmbedMessage(event) {
      if (!this.signedUrl || event.data?.source !== 'quibot-embed') {
        return;
      }
      // Solo aceptamos el aviso desde el mismo origen que la herramienta embebida.
      try {
        if (event.origin !== new URL(this.signedUrl).origin) {
          return;
        }
      } catch (error) {
        return;
      }
      // Falla permanente (sin tenant, firma inválida, etc.): una URL firmada
      // nueva tendría el mismo problema, así que no reintentamos — mostramos
      // directamente el estado de error nativo en vez del iframe.
      if (event.data.type === 'session-unavailable') {
        this.signedUrl = '';
        this.hasError = true;
        return;
      }

      if (event.data.type !== 'session-expired') {
        return;
      }

      const now = Date.now();
      if (
        this.retryCount >= MAX_AUTO_RETRIES ||
        now - this.lastRetryAt < RETRY_COOLDOWN_MS
      ) {
        this.signedUrl = '';
        this.hasError = true;
        return;
      }
      this.retryCount += 1;
      this.lastRetryAt = now;
      this.fetchSignedUrl();
    },
  },
};
</script>

<template>
  <div class="module-frame--container">
    <iframe
      v-if="signedUrl"
      class="module-frame--iframe"
      :src="signedUrl"
      :title="currentModule.name"
    />
    <p v-else-if="hasError" class="module-frame--error">
      {{ $t('SIDEBAR.CUSTOM_MODULE_LOAD_ERROR') }}
    </p>
  </div>
</template>

<style scoped>
.module-frame--container,
.module-frame--iframe {
  width: 100%;
  height: 100%;
}

.module-frame--iframe {
  border: 0;
  display: block;
}

.module-frame--error {
  padding: var(--space-medium);
  text-align: center;
}
</style>
